import { ActionIcon, Alert, Group, Paper, Skeleton, Text, Title } from "@mantine/core";
import { IconGripVertical, IconX } from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { DashboardInterpretation, DashboardReportMeta } from "../../types/api";
import { axioTokens } from "../../theme";
import { shortInsight } from "./interpretationHint";

const STATUS_COLORS: Record<DashboardInterpretation["status"], string> = {
  ok: "green",
  warning: "yellow",
  critical: "red",
};

interface DashboardWidgetShellProps {
  report: DashboardReportMeta;
  interpretation?: DashboardInterpretation | null;
  loading?: boolean;
  error?: string | null;
  editMode?: boolean;
  onRemove?: () => void;
  children: ReactNode;
}

export function DashboardWidgetShell({
  report,
  interpretation,
  loading,
  error,
  editMode,
  onRemove,
  children,
}: DashboardWidgetShellProps) {
  const hint = interpretation ? shortInsight(interpretation) : null;
  const hintColor = interpretation ? STATUS_COLORS[interpretation.status] : "dimmed";
  const fillsHeight = report.element_type !== "infocard";

  return (
    <Paper
      withBorder

      p="lg"
      h="100%"
      className="dashboard-widget"
      style={{ borderColor: axioTokens.border, boxShadow: "none" }}
    >
      <Group justify="space-between" align="center" gap="xs" mb="sm" wrap="nowrap">
        <Group gap="var(--axio-unit-6)" wrap="nowrap" style={{ flex: "var(--axio-number-one)", minWidth: "var(--axio-unit-0)" }}>
          {editMode && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              className="dashboard-drag-handle"
              aria-label="Drag widget"
            >
              <IconGripVertical size="var(--axio-unit-16)" />
            </ActionIcon>
          )}
          <Title order={5} lineClamp={1} style={{ flex: "var(--axio-number-one)", minWidth: "var(--axio-unit-0)", fontWeight: "var(--axio-font-weight-semibold)" }}>
            {report.title}
          </Title>
        </Group>
        <Group gap="var(--axio-unit-4)" wrap="nowrap">
          {hint && (
            <Text size="xs" c={hintColor} lineClamp={1} ta="right" maw="var(--axio-unit-140)">
              {hint}
            </Text>
          )}
          {editMode && onRemove && (
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              aria-label="Remove widget"
              onClick={onRemove}
            >
              <IconX size="var(--axio-unit-14)" />
            </ActionIcon>
          )}
        </Group>
      </Group>

      <div
        className={
          fillsHeight ? "dashboard-widget__body dashboard-widget__body--fill" : "dashboard-widget__body"
        }
      >
        {loading ? (
          <>
            <Skeleton height={fillsHeight ? axioTokens.percentage.full : axioTokens.unit[36]} width={fillsHeight ? axioTokens.percentage.full : axioTokens.percentage.twoFifths} />
          </>
        ) : error ? (
          <Alert color="red" title="Could not load" py="xs">
            {error}
          </Alert>
        ) : (
          children
        )}
      </div>
    </Paper>
  );
}
